import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight, Shield, Sparkles, Lock, Search, Shuffle, Eye,
  Zap, Check, X, Play, Menu, X as XIcon, ChevronDown,
  Layers, HardDrive, Star, ArrowUpRight
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
//  DESIGN 1: "AURORA DRIFT"
//
//  A cinematic, scroll-driven narrative homepage with floating
//  aurora gradients, parallax depth layers, and cinematic
//  reveal animations. Matches the Ethereal Glass aesthetic.
// ─────────────────────────────────────────────────────────────────

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Outfit', sans-serif";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Outfit:wght@100;200;300;400;500;600&display=swap');
  
  @keyframes auroraShift1 { 0%{transform:translate(-10%,-10%) rotate(0deg) scale(1)} 33%{transform:translate(5%,15%) rotate(60deg) scale(1.2)} 66%{transform:translate(-15%,5%) rotate(120deg) scale(0.9)} 100%{transform:translate(-10%,-10%) rotate(360deg) scale(1)} }
  @keyframes auroraShift2 { 0%{transform:translate(10%,10%) rotate(0deg) scale(1.1)} 33%{transform:translate(-10%,-5%) rotate(-40deg) scale(0.8)} 66%{transform:translate(15%,-10%) rotate(-120deg) scale(1.15)} 100%{transform:translate(10%,10%) rotate(-360deg) scale(1.1)} }
  @keyframes auroraShift3 { 0%{transform:translate(0%,0%) scale(1)} 50%{transform:translate(-20%,10%) scale(1.3)} 100%{transform:translate(0%,0%) scale(1)} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes scaleIn { from{opacity:0;transform:scale(0.9)} to{opacity:1;transform:scale(1)} }
  @keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:0.8} }
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
  @keyframes scrollHint { 0%,100%{transform:translateY(0);opacity:0.4} 50%{transform:translateY(8px);opacity:0.8} }
  @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  @keyframes gradientText { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  @keyframes borderGlow { 0%,100%{border-color:rgba(196,181,253,0.1)} 50%{border-color:rgba(196,181,253,0.3)} }

  .aurora-text {
    background: linear-gradient(135deg, #C4B5FD, #FDA4AF, #A5F3FC, #C4B5FD);
    background-size: 300% 300%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gradientText 8s ease infinite;
  }
  .glass-card {
    background: rgba(255,255,255,0.03);
    backdrop-filter: blur(40px) saturate(1.5);
    -webkit-backdrop-filter: blur(40px) saturate(1.5);
    border: 1px solid rgba(255,255,255,0.06);
  }
  .glass-card-hover:hover {
    background: rgba(255,255,255,0.06);
    border-color: rgba(196,181,253,0.2);
    transform: translateY(-4px);
  }
  .glass-nav {
    background: rgba(12,10,20,0.8);
    backdrop-filter: blur(40px) saturate(1.8);
    -webkit-backdrop-filter: blur(40px) saturate(1.8);
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
`;

// ─── Intersection Observer hook for scroll animations ───
const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
};

// ─── Aurora background ───
const AuroraBackground: React.FC = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
    <div className="absolute w-[800px] h-[800px] rounded-full opacity-[0.07]"
      style={{ top: '-20%', left: '-10%', background: 'radial-gradient(circle, #C4B5FD 0%, transparent 70%)', animation: 'auroraShift1 25s ease-in-out infinite' }} />
    <div className="absolute w-[600px] h-[600px] rounded-full opacity-[0.05]"
      style={{ top: '30%', right: '-15%', background: 'radial-gradient(circle, #FDA4AF 0%, transparent 70%)', animation: 'auroraShift2 30s ease-in-out infinite' }} />
    <div className="absolute w-[700px] h-[700px] rounded-full opacity-[0.06]"
      style={{ bottom: '-10%', left: '20%', background: 'radial-gradient(circle, #A5F3FC 0%, transparent 70%)', animation: 'auroraShift3 22s ease-in-out infinite' }} />
    {/* Noise overlay */}
    <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")` }} />
  </div>
);

// ─── Feature card ───
const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; desc: string; delay: number }> = ({ icon, title, desc, delay }) => {
  const { ref, inView } = useInView();
  return (
    <div ref={ref} className="glass-card glass-card-hover rounded-3xl p-8 transition-all duration-500 cursor-default"
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${delay}ms` }}>
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
        style={{ background: 'linear-gradient(135deg, rgba(196,181,253,0.15), rgba(196,181,253,0.05))', border: '1px solid rgba(196,181,253,0.15)' }}>
        {icon}
      </div>
      <h3 className="text-xl font-medium text-white mb-3" style={{ fontFamily: SANS }}>{title}</h3>
      <p className="text-white/30 text-sm leading-relaxed" style={{ fontFamily: SANS, fontWeight: 200 }}>{desc}</p>
    </div>
  );
};

// ─── Step ───
const Step: React.FC<{ num: number; title: string; desc: string; accent: string }> = ({ num, title, desc, accent }) => {
  const { ref, inView } = useInView();
  return (
    <div ref={ref} className="flex gap-6 items-start"
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)', transitionDelay: `${num * 150}ms` }}>
      <div className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-light"
        style={{ background: `linear-gradient(135deg, ${accent}30, ${accent}10)`, border: `1px solid ${accent}25`, color: accent, fontFamily: SERIF }}>
        {num}
      </div>
      <div>
        <h3 className="text-2xl font-light text-white mb-2" style={{ fontFamily: SERIF }}>{title}</h3>
        <p className="text-white/25 text-base leading-relaxed" style={{ fontFamily: SANS, fontWeight: 200 }}>{desc}</p>
      </div>
    </div>
  );
};

// ─── Pricing card ───
const PricingCard: React.FC<{
  tier: string; price: string; period: string; yearlyNote?: string;
  features: { text: string; included: boolean }[];
  accent: string; highlighted?: boolean; cta: string; onClick: () => void; delay: number;
}> = ({ tier, price, period, yearlyNote, features, accent, highlighted, cta, onClick, delay }) => {
  const { ref, inView } = useInView();
  return (
    <div ref={ref}
      className={`relative rounded-3xl p-8 transition-all duration-700 ${highlighted ? '' : 'glass-card glass-card-hover'}`}
      style={{
        opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${delay}ms`,
        ...(highlighted ? {
          background: 'linear-gradient(135deg, rgba(196,181,253,0.08), rgba(196,181,253,0.02))',
          border: `1px solid rgba(196,181,253,0.2)`,
          boxShadow: '0 0 60px rgba(196,181,253,0.08)',
        } : {}),
      }}>
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1.5 rounded-full text-[11px] font-medium tracking-wider uppercase"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}80)`, color: '#0C0A14', fontFamily: SANS }}>
            Most Popular
          </span>
        </div>
      )}
      <h3 className="text-2xl font-light text-white mb-1" style={{ fontFamily: SERIF }}>{tier}</h3>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-4xl font-light" style={{ fontFamily: SERIF, color: accent }}>{price}</span>
        <span className="text-white/20 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>/{period}</span>
      </div>
      {yearlyNote && <p className="text-white/15 text-xs mb-6" style={{ fontFamily: SANS, fontWeight: 200 }}>{yearlyNote}</p>}
      {!yearlyNote && <div className="mb-6" />}
      <ul className="space-y-3 mb-8">
        {features.map((f, i) => (
          <li key={i} className="flex items-center gap-3 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>
            {f.included
              ? <Check size={14} style={{ color: accent }} className="flex-shrink-0" />
              : <X size={14} className="text-white/10 flex-shrink-0" />}
            <span className={f.included ? 'text-white/40' : 'text-white/15'}>{f.text}</span>
          </li>
        ))}
      </ul>
      <button onClick={onClick}
        className="w-full py-3.5 rounded-2xl text-sm font-medium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
        style={highlighted
          ? { background: `linear-gradient(135deg, ${accent}, ${accent}80)`, color: '#0C0A14', fontFamily: SANS }
          : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', fontFamily: SANS }
        }>
        {cta}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

const HomepageDesign1: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 100);
    const handleMouse = (e: MouseEvent) => setMousePos({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight });
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  const handleGetStarted = () => { window.location.href = '/'; };

  // Smooth scroll
  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen text-white overflow-x-hidden" style={{ background: '#0C0A14' }}>
      <style>{STYLES}</style>
      <AuroraBackground />

      {/* ─── Navigation ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/refract-logo.png" alt="Refract" className="w-10 h-10 rounded-lg" />
            <span className="text-lg font-light tracking-wide" style={{ fontFamily: SERIF }}>Refract</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo('features')} className="text-white/30 hover:text-white/70 transition-colors text-sm" style={{ fontFamily: SANS, fontWeight: 300 }}>Features</button>
            <button onClick={() => scrollTo('how-it-works')} className="text-white/30 hover:text-white/70 transition-colors text-sm" style={{ fontFamily: SANS, fontWeight: 300 }}>How It Works</button>
            <button onClick={() => scrollTo('pricing')} className="text-white/30 hover:text-white/70 transition-colors text-sm" style={{ fontFamily: SANS, fontWeight: 300 }}>Pricing</button>
            <button onClick={handleGetStarted}
              className="px-6 py-2.5 rounded-2xl text-sm font-medium transition-all duration-300 hover:scale-[1.03]"
              style={{ background: 'linear-gradient(135deg, #C4B5FD, #C4B5FD80)', color: '#0C0A14', fontFamily: SANS }}>
              Get Started
            </button>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white/40 hover:text-white">
            {menuOpen ? <XIcon size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden px-6 py-6 space-y-4 border-t border-white/5" style={{ background: 'rgba(12,10,20,0.95)' }}>
            <button onClick={() => scrollTo('features')} className="block text-white/40 hover:text-white text-sm w-full text-left" style={{ fontFamily: SANS }}>Features</button>
            <button onClick={() => scrollTo('how-it-works')} className="block text-white/40 hover:text-white text-sm w-full text-left" style={{ fontFamily: SANS }}>How It Works</button>
            <button onClick={() => scrollTo('pricing')} className="block text-white/40 hover:text-white text-sm w-full text-left" style={{ fontFamily: SANS }}>Pricing</button>
            <button onClick={handleGetStarted} className="w-full py-3 rounded-2xl text-sm font-medium" style={{ background: 'linear-gradient(135deg, #C4B5FD, #C4B5FD80)', color: '#0C0A14', fontFamily: SANS }}>Get Started</button>
          </div>
        )}
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20">
        {/* Parallax floating elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-1 h-1 rounded-full bg-[#C4B5FD]/20" style={{ top: '20%', left: '15%', animation: 'float 6s ease-in-out infinite', transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)` }} />
          <div className="absolute w-1.5 h-1.5 rounded-full bg-[#FDA4AF]/20" style={{ top: '40%', right: '20%', animation: 'float 8s ease-in-out infinite 1s', transform: `translate(${mousePos.x * -15}px, ${mousePos.y * -15}px)` }} />
          <div className="absolute w-1 h-1 rounded-full bg-[#A5F3FC]/20" style={{ bottom: '30%', left: '25%', animation: 'float 7s ease-in-out infinite 0.5s', transform: `translate(${mousePos.x * 12}px, ${mousePos.y * -12}px)` }} />
          <div className="absolute w-0.5 h-0.5 rounded-full bg-[#FDE68A]/30" style={{ top: '60%', right: '35%', animation: 'float 9s ease-in-out infinite 2s' }} />
          <div className="absolute w-0.5 h-0.5 rounded-full bg-[#C4B5FD]/15" style={{ top: '75%', left: '40%', animation: 'float 5s ease-in-out infinite 1.5s' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 1s cubic-bezier(0.16,1,0.3,1)' }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[.2em] uppercase mb-8 glass-card"
              style={{ fontFamily: SANS, fontWeight: 300, color: 'rgba(196,181,253,0.6)' }}>
              <Sparkles size={12} /> Private Media Rediscovery
            </span>
          </div>

          {/* Title */}
          <h1 className="mb-6" style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1) 0.15s' }}>
            <span className="block text-6xl md:text-8xl font-light leading-[0.95] tracking-tight" style={{ fontFamily: SERIF }}>
              Rediscover the
            </span>
            <span className="block text-6xl md:text-8xl font-light leading-[0.95] tracking-tight aurora-text mt-2" style={{ fontFamily: SERIF }}>
              moments you forgot
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-white/25 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ fontFamily: SANS, fontWeight: 200, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(25px)', transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1) 0.3s' }}>
            Shuffle through photos and videos from your library. AI-powered search.
            Full privacy. Your content, untouched and rediscovered.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
            style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1) 0.45s' }}>
            <button onClick={handleGetStarted}
              className="px-10 py-4 rounded-2xl text-base font-medium transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] flex items-center justify-center gap-3"
              style={{ background: 'linear-gradient(135deg, #C4B5FD, #C4B5FD80)', color: '#0C0A14', fontFamily: SANS, boxShadow: '0 8px 40px rgba(196,181,253,0.2)' }}>
              Start Exploring <ArrowRight size={18} />
            </button>
            <button onClick={() => scrollTo('how-it-works')}
              className="px-10 py-4 rounded-2xl text-base font-medium transition-all duration-300 hover:scale-[1.02] glass-card"
              style={{ color: 'rgba(255,255,255,0.5)', fontFamily: SANS }}>
              Learn More
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-[11px] text-white/15"
            style={{ fontFamily: SANS, fontWeight: 200, opacity: heroVisible ? 1 : 0, transition: 'all 1.2s cubic-bezier(0.16,1,0.3,1) 0.6s' }}>
            <span className="flex items-center gap-1.5"><Lock size={11} className="text-[#C4B5FD]/40" /> Local-first storage</span>
            <span className="text-white/5">·</span>
            <span className="flex items-center gap-1.5"><Shield size={11} className="text-[#C4B5FD]/40" /> No tracking</span>
            <span className="text-white/5">·</span>
            <span>Free tier available</span>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2" style={{ animation: 'scrollHint 2s ease-in-out infinite' }}>
          <ChevronDown size={20} className="text-white/10" />
        </div>
      </section>

      {/* ─── Social Proof ─── */}
      <section className="py-20 px-6 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <div className="glass-card rounded-3xl p-10">
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} size={14} className="text-[#FDE68A]/60" fill="rgba(253,230,138,0.6)" />)}
            </div>
            <p className="text-white/30 text-lg italic leading-relaxed mb-4" style={{ fontFamily: SERIF, fontWeight: 300 }}>
              "Finally, a media viewer that actually respects my privacy. The shuffle feature helped me rediscover photos I'd completely forgotten about."
            </p>
            <p className="text-white/15 text-xs tracking-wider uppercase" style={{ fontFamily: SANS, fontWeight: 300 }}>— Creator & Indie Developer</p>
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section id="features" className="py-28 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <SectionHeading
              eyebrow="Features"
              title="Built for discovery,"
              titleAccent="designed for you"
              subtitle="Everything you need to explore your collection, with complete control over your data."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard icon={<Lock size={20} className="text-[#C4B5FD]" />} title="Your Media, Your Storage" desc="Photos and videos stay in Google Drive or your browser. We only store a small index to remember your selections." delay={0} />
            <FeatureCard icon={<Search size={20} className="text-[#FDA4AF]" />} title="Find Anything, Instantly" desc="Ask &quot;sunset photos from last summer&quot; and find exactly what you're looking for. Powered by AI." delay={100} />
            <FeatureCard icon={<Shuffle size={20} className="text-[#A5F3FC]" />} title="Rediscover Forgotten Memories" desc="Shuffle through your entire collection. Surface photos and videos you forgot you had." delay={200} />
            <FeatureCard icon={<Eye size={20} className="text-[#FDE68A]" />} title="Distraction-Free Viewing" desc="Full-screen player with customizable playback. Focus on your content, nothing else." delay={300} />
            <FeatureCard icon={<Shield size={20} className="text-[#6EE7B7]" />} title="Privacy by Design" desc="Your photos and videos are never uploaded to our servers. Media streams directly from Drive to your device." delay={400} />
            <FeatureCard icon={<Zap size={20} className="text-[#DDD6FE]" />} title="Incremental Sync" desc="Fast, efficient syncing that only updates changed files. No full re-scans needed." delay={500} />
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section id="how-it-works" className="py-28 px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-20">
            <SectionHeading
              eyebrow="How It Works"
              title="Three steps to"
              titleAccent="rediscovery"
              subtitle="Simple, private, powerful."
            />
          </div>
          <div className="space-y-10">
            <Step num={1} title="Add Your Media" desc="Connect your Google Drive or upload files directly from your device. Everything stays local—nothing is uploaded to our servers." accent="#C4B5FD" />
            <div className="ml-7 border-l border-white/5 h-6" />
            <Step num={2} title="AI Analyzes Your Collection" desc="Our AI generates search tags and metadata. Processing happens through secure APIs—your files never leave your device." accent="#FDA4AF" />
            <div className="ml-7 border-l border-white/5 h-6" />
            <Step num={3} title="Start Exploring" desc="Shuffle through your collection, search with natural language, or filter by type. Rediscover memories you forgot you had." accent="#A5F3FC" />
          </div>
        </div>
      </section>

      {/* ─── Privacy ─── */}
      <section className="py-28 px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <SectionHeading
                eyebrow="Privacy"
                title="You own"
                titleAccent="every pixel"
                subtitle="Freedom to organize your media, your way. No compromises."
                align="left"
              />
              <ul className="space-y-5 mt-10">
                {[
                  { title: 'Your Media, Your Storage', desc: 'Photos and videos stay in Drive or your browser. We store only a lightweight index for cross-session sync.' },
                  { title: 'No Unnecessary Tracking', desc: "We don't track your browsing. Your viewing habits stay private." },
                  { title: 'Your Cloud, Your Choice', desc: 'Connect your own cloud storage. Optional sync, always your control.' },
                  { title: 'Secure by Default', desc: 'Secure authentication. Your data, your keys, your control.' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: 'rgba(196,181,253,0.1)' }}>
                      <Check size={12} className="text-[#C4B5FD]" />
                    </div>
                    <div>
                      <h4 className="text-white/60 text-sm font-medium mb-1" style={{ fontFamily: SANS }}>{item.title}</h4>
                      <p className="text-white/20 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass-card rounded-3xl p-10 space-y-8">
              {[
                { icon: <Lock size={22} className="text-[#C4B5FD]" />, text: 'Local-First Storage' },
                { icon: <Shield size={22} className="text-[#FDA4AF]" />, text: 'You Control Your Data' },
                { icon: <Eye size={22} className="text-[#A5F3FC]" />, text: 'No Unnecessary Tracking' },
                { icon: <HardDrive size={22} className="text-[#FDE68A]" />, text: 'Secure Cloud Integration' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 text-white/40" style={{ fontFamily: SANS, fontWeight: 300 }}>
                  {item.icon}
                  <span className="text-lg">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-28 px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <SectionHeading
              eyebrow="Pricing"
              title="Simple,"
              titleAccent="transparent pricing"
              subtitle="Start free. Upgrade when you need more."
            />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <PricingCard
              tier="Free" price="$0" period="forever" delay={0}
              features={[
                { text: '2,000 Drive items', included: true },
                { text: 'Unlimited local files', included: true },
                { text: 'Random shuffle', included: true },
                { text: 'Basic keyword search', included: true },
                { text: 'AI search tags', included: false },
                { text: 'Collections', included: false },
              ]}
              accent="#C4B5FD" cta="Get Started" onClick={handleGetStarted}
            />
            <PricingCard
              tier="Pro" price="$5.99" period="month" yearlyNote="or $57.99/year (first 200: $4.99/mo)" highlighted delay={150}
              features={[
                { text: '50,000 Drive items', included: true },
                { text: 'Unlimited local files', included: true },
                { text: 'AI search tags', included: true },
                { text: 'Collections (up to 50)', included: true },
                { text: 'Cloud backup', included: true },
                { text: 'All Free features', included: true },
              ]}
              accent="#C4B5FD" cta="Start Pro Trial" onClick={handleGetStarted}
            />
            <PricingCard
              tier="Power" price="$19.99" period="month" yearlyNote="or $199.99/year" delay={300}
              features={[
                { text: '200,000 Drive items', included: true },
                { text: 'Unlimited local files', included: true },
                { text: 'AI search tags', included: true },
                { text: 'Collections (up to 500)', included: true },
                { text: 'Premium features', included: true },
                { text: 'All Pro features', included: true },
              ]}
              accent="#A78BFA" cta="Start Power Trial" onClick={handleGetStarted}
            />
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-32 px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-light mb-6" style={{ fontFamily: SERIF }}>
            Ready to <span className="aurora-text">rediscover</span> your collection?
          </h2>
          <p className="text-white/20 text-lg mb-10" style={{ fontFamily: SANS, fontWeight: 200 }}>
            Start browsing your media library in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={handleGetStarted}
              className="px-10 py-4 rounded-2xl text-base font-medium transition-all duration-300 hover:scale-[1.03] flex items-center justify-center gap-3"
              style={{ background: 'linear-gradient(135deg, #C4B5FD, #C4B5FD80)', color: '#0C0A14', fontFamily: SANS, boxShadow: '0 8px 40px rgba(196,181,253,0.2)' }}>
              Get Started Free <ArrowRight size={18} />
            </button>
          </div>
          <p className="text-white/10 text-xs mt-8" style={{ fontFamily: SANS, fontWeight: 200 }}>
            No credit card required · Free tier available · You control your data
          </p>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-white/5 py-16 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="text-white/30 text-[10px] tracking-[.25em] uppercase mb-5" style={{ fontFamily: SANS, fontWeight: 300 }}>Product</h4>
              <ul className="space-y-3">
                {['Features', 'How It Works', 'Pricing'].map(t => (
                  <li key={t}><button onClick={() => scrollTo(t.toLowerCase().replace(/\s/g, '-'))} className="text-white/15 hover:text-white/40 text-sm transition-colors" style={{ fontFamily: SANS, fontWeight: 200 }}>{t}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white/30 text-[10px] tracking-[.25em] uppercase mb-5" style={{ fontFamily: SANS, fontWeight: 300 }}>Resources</h4>
              <ul className="space-y-3 text-white/15 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>
                <li><a href="#" className="hover:text-white/40 transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white/40 transition-colors">API Reference</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white/30 text-[10px] tracking-[.25em] uppercase mb-5" style={{ fontFamily: SANS, fontWeight: 300 }}>Legal</h4>
              <ul className="space-y-3 text-white/15 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>
                <li><a href="/privacy" className="hover:text-white/40 transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white/40 transition-colors">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white/30 text-[10px] tracking-[.25em] uppercase mb-5" style={{ fontFamily: SANS, fontWeight: 300 }}>Connect</h4>
              <ul className="space-y-3 text-white/15 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>
                <li><a href="#" className="hover:text-white/40 transition-colors">Twitter</a></li>
                <li><a href="#" className="hover:text-white/40 transition-colors">GitHub</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/refract-logo.png" alt="Refract" className="w-7 h-7 rounded-md opacity-30" />
              <span className="text-white/15 text-sm" style={{ fontFamily: SANS, fontWeight: 200 }}>© 2026 Refract. All rights reserved.</span>
            </div>
            <p className="text-white/10 text-xs" style={{ fontFamily: SANS, fontWeight: 200 }}>Made for creators, by creators.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ─── Section heading helper ───
const SectionHeading: React.FC<{ eyebrow: string; title: string; titleAccent: string; subtitle: string; align?: 'center' | 'left' }> = ({ eyebrow, title, titleAccent, subtitle, align = 'center' }) => {
  const { ref, inView } = useInView();
  return (
    <div ref={ref} className={align === 'center' ? 'text-center' : ''}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}>
      <span className="text-[10px] tracking-[.3em] uppercase mb-4 block" style={{ fontFamily: SANS, fontWeight: 300, color: 'rgba(196,181,253,0.4)' }}>{eyebrow}</span>
      <h2 className="text-4xl md:text-5xl font-light mb-4" style={{ fontFamily: SERIF }}>
        {title} <span className="aurora-text">{titleAccent}</span>
      </h2>
      <p className={`text-white/20 text-lg ${align === 'center' ? 'max-w-2xl mx-auto' : ''}`} style={{ fontFamily: SANS, fontWeight: 200 }}>{subtitle}</p>
    </div>
  );
};

export default HomepageDesign1;
